import { useCallback, useEffect, useState } from 'react'

interface UseCountdownProps {
  initialSeconds?: number
  onComplete?: () => void
  autoStart?: boolean
}

interface UseCountdownReturn {
  seconds: number
  isActive: boolean
  start: () => void
  pause: () => void
  reset: () => void
  setSeconds: (seconds: number) => void
}

export function useCountdown({
  initialSeconds = 60,
  onComplete,
  autoStart = true,
}: UseCountdownProps = {}): UseCountdownReturn {
  const [seconds, setSeconds] = useState(initialSeconds)
  const [isActive, setIsActive] = useState(autoStart)

  useEffect(() => {
    let interval: NodeJS.Timeout

    if (isActive && seconds > 0) {
      interval = setInterval(() => {
        setSeconds((prev) => {
          if (prev <= 1) {
            setIsActive(false)
            onComplete?.()
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }

    return () => clearInterval(interval)
  }, [isActive, seconds, onComplete])

  const start = useCallback(() => setIsActive(true), [])
  const pause = useCallback(() => setIsActive(false), [])
  const reset = useCallback(() => {
    setSeconds(initialSeconds)
    setIsActive(autoStart)
  }, [initialSeconds, autoStart])

  return {
    seconds,
    isActive,
    start,
    pause,
    reset,
    setSeconds,
  }
}
